import {Message} from "../types";

export const formatBytes = (bytes: number, decimals = 2) => {
    if (bytes === 0) return '0 Bytes';

    const k = 1024;
    const dm = decimals < 0 ? 0 : decimals;
    const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB', 'PB'];

    const i = Math.floor(Math.log(bytes) / Math.log(k));

    return parseFloat((bytes / Math.pow(k, i)).toFixed(dm)) + ' ' + sizes[i];
}

export const parseTimestamp = (timestamp: string) => {
    if (!timestamp) return new Date();

    if (timestamp.includes(' ')) {
        const isoString = timestamp.replace(' ', 'T');
        return new Date(isoString);
    }

    return new Date(timestamp);
};

export const formatDateDivider = (date: Date) => {
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);

    const messageDate = new Date(date.getFullYear(), date.getMonth(), date.getDate());

    if (messageDate.getTime() === today.getTime()) {
        return '今日';
    }

    if (messageDate.getTime() === yesterday.getTime()) {
        return '昨日';
    }

    const daysDiff = Math.floor((today.getTime() - messageDate.getTime()) / (1000 * 60 * 60 * 24));
    if (daysDiff < 7) {
        const weekdays = ['日曜日', '月曜日', '火曜日', '水曜日', '木曜日', '金曜日', '土曜日'];
        return weekdays[date.getDay()];
    }

    if (date.getFullYear() === now.getFullYear()) {
        return date.toLocaleDateString('en-US', { day: 'numeric', month: 'long' });
    }

    return date.toLocaleDateString('en-US', { day: 'numeric', month: 'long', year: 'numeric' });
};

export const formatTime = (date: Date) => {
    return date.toLocaleTimeString('en-US', {
        hour: 'numeric',
        minute: '2-digit',
        hour12: true
    });
};

export const shouldGroupWithPrevious = (
    currentMsg: Message,
    previousMsg: Message,
    groupTimeThreshold = 10 * 60 * 1000
) => {
    if (!previousMsg) return false;

    const sameSender = currentMsg.sender_id === previousMsg.sender_id;

    const currentTime = parseTimestamp(currentMsg.created_at);
    const previousTime = parseTimestamp(previousMsg.created_at);
    const timeDiff = currentTime.getTime() - previousTime.getTime();
    const withinTimeThreshold = timeDiff <= groupTimeThreshold;

    return sameSender && withinTimeThreshold;
};

export const shouldShowDateDivider = (currentMsg: Message, previousMsg: Message | null) => {
    if (!previousMsg) return true;

    const currentDate = parseTimestamp(currentMsg.created_at);
    const previousDate = parseTimestamp(previousMsg.created_at);

    return currentDate.toDateString() !== previousDate.toDateString();
};

export const shouldShowTimeDivider = (
    currentMsg: Message,
    previousMsg: Message | null,
    timeDividerThreshold = 30 * 60 * 1000 // 30 minutes default
) => {
    if (!previousMsg) return false;

    const currentTime = parseTimestamp(currentMsg.created_at);
    const previousTime = parseTimestamp(previousMsg.created_at);

    if (currentTime.toDateString() !== previousTime.toDateString()) {
        return false;
    }

    const timeDiff = currentTime.getTime() - previousTime.getTime();
    return timeDiff > timeDividerThreshold;
};

export interface MessageGroup {
    messages: Message[];
    showDateDivider: boolean;
    dateDividerText?: string;
    showTimeDivider: boolean;
    timeDividerText?: string;
}

export const groupMessages = (
    messages: Message[],
    groupTimeThreshold = 10 * 60 * 1000,
    timeDividerThreshold = 30 * 60 * 1000
): MessageGroup[] => {
    if (!messages || messages.length === 0) return [];

    const sortedMessages = [...messages].sort((a, b) => {
        const timeA = parseTimestamp(a.created_at);
        const timeB = parseTimestamp(b.created_at);
        return timeA.getTime() - timeB.getTime();
    });

    const groups: MessageGroup[] = [];
    let currentGroup = [sortedMessages[0]];

    for (let i = 1; i < sortedMessages.length; i++) {
        const currentMsg = sortedMessages[i];
        const previousMsg = sortedMessages[i - 1];

        if (shouldGroupWithPrevious(currentMsg, previousMsg, groupTimeThreshold)) {
            currentGroup.push(currentMsg);
        } else {
            const previousGroup = groups.length > 0 ? groups[groups.length - 1] : null;
            const showDateDiv = !previousGroup ||
                shouldShowDateDivider(currentGroup[0], previousGroup.messages[0]);
            const showTimeDiv = previousGroup && !showDateDiv &&
                shouldShowTimeDivider(currentGroup[0], previousGroup.messages[previousGroup.messages.length - 1], timeDividerThreshold);

            groups.push({
                messages: [...currentGroup],
                showDateDivider: showDateDiv,
                dateDividerText: showDateDiv ? formatDateDivider(parseTimestamp(currentGroup[0].created_at)) : undefined,
                showTimeDivider: !!showTimeDiv,
                timeDividerText: showTimeDiv ? formatTime(parseTimestamp(currentGroup[0].created_at)) : undefined
            });

            currentGroup = [currentMsg];
        }
    }

    if (currentGroup.length > 0) {
        const previousGroup = groups.length > 0 ? groups[groups.length - 1] : null;
        const showDateDiv = !previousGroup ||
            shouldShowDateDivider(currentGroup[0], previousGroup.messages[0]);
        const showTimeDiv = previousGroup && !showDateDiv &&
            shouldShowTimeDivider(currentGroup[0], previousGroup.messages[previousGroup.messages.length - 1], timeDividerThreshold);

        groups.push({
            messages: currentGroup,
            showDateDivider: showDateDiv,
            dateDividerText: showDateDiv ? formatDateDivider(parseTimestamp(currentGroup[0].created_at)) : undefined,
            showTimeDivider: !!showTimeDiv,
            timeDividerText: showTimeDiv ? formatTime(parseTimestamp(currentGroup[0].created_at)) : undefined
        });
    }

    return groups;
};

export const getRole = (role: string) => {
    switch (role) {
        case 'admin': return 'アドミン';
        case 'user': return 'ユーザー';
        case 'super_admin': return 'スーパーアドミン';
        default: return 'N/A';
    }
}

export function isValidURL(string: string) {
    try {
        const url = new URL(string);
        return url.protocol === 'http:' || url.protocol === 'https:';
    } catch (err) {
        return false;
    }
}

export function extractURL(text: string) {
    const urlRegex = /(?:https?:\/\/)?(?:www\.)?[-a-zA-Z0-9@:%._\+~#=]{2,256}\.[a-zA-Z]{2,6}\b(?:[-a-zA-Z0-9()@:%_\+.~#?&\/=]*)/gi;
    const matches = text.match(urlRegex);

    if (!matches) return [];

    return matches.filter(url => {
        const domain = url.split('/')[0].split('?')[0];
        return /[a-zA-Z]/.test(domain);
    });
}

export function normalizeURL(url: string) {
    let normalized = url.trim();

    if (!normalized.match(/^https?:\/\//i)) {
        normalized = 'https://' + normalized;
    }

    if (!isValidURL(normalized)) {
        return null;
    }

    return normalized;
}

export const getInitials = (name: string): string => {
    if (!name) return 'U';

    const cleanName = name.replace(/[-@#$%&*()[\]{}<>/\\|,.;:"'`~]/g, '').trim();

    if (!cleanName) return 'U';

    const hasJapanese = /[\u3040-\u309F\u30A0-\u30FF\u4E00-\u9FAF]/.test(cleanName);

    if (hasJapanese) {
        const words = cleanName.split(/\s+/);
        const firstWord = words[0];
        return firstWord.substring(0, 2).toUpperCase();
    } else {
        const words = cleanName.split(/\s+/);

        if (words.length >= 2) {
            return (words[0][0] + words[1][0]).toUpperCase();
        }

        return cleanName.substring(0, 2).toUpperCase();
    }
};

